import { createSlice } from '@reduxjs/toolkit';

const migrationSlice = createSlice({
    name: 'migration',
    initialState: {
        sources: [],           // Array of { platform, path }
        targets: [],           // Array of platform strings
        outputDir: '',         // Output directory for migration
        analysisResult: null,  // { sources, targets, totalTracks, totalPlaylists, totalCuePoints, errors }
        migrationProgress: 0,
        isAnalyzing: false,
        isMigrating: false,
        cancelRequested: false,
        migrationLogs: [],     // Array of per-track results { filePath, trackName, status, message }
        errors: [],            // Array of error strings
    },
    reducers: {
        addSource: (state, action) => {
            // payload: { platform, path }
            const { platform } = action.payload;
            const existing = state.sources.find(s => s.platform === platform);
            if (!existing) {
                state.sources.push(action.payload);
            }
        },
        removeSource: (state, action) => {
            // payload: platform string
            state.sources = state.sources.filter(s => s.platform !== action.payload);
        },
        setSourcePath: (state, action) => {
            // payload: { platform, path }
            const { platform, path } = action.payload;
            const existing = state.sources.find(s => s.platform === platform);
            if (existing) {
                existing.path = path;
            } else {
                state.sources.push({ platform, path });
            }
        },
        setSources: (state, action) => {
            state.sources = action.payload;
        },
        addTarget: (state, action) => {
            // payload: platform string
            if (!state.targets.includes(action.payload)) {
                state.targets.push(action.payload);
            }
        },
        removeTarget: (state, action) => {
            // payload: platform string
            state.targets = state.targets.filter(t => t !== action.payload);
        },
        setTargets: (state, action) => {
            state.targets = action.payload;
        },
        setOutputDir: (state, action) => {
            state.outputDir = action.payload;
        },
        setAnalysisResult: (state, action) => {
            state.analysisResult = action.payload;
        },
        setMigrationProgress: (state, action) => {
            state.migrationProgress = action.payload;
        },
        setIsAnalyzing: (state, action) => {
            state.isAnalyzing = action.payload;
        },
        setIsMigrating: (state, action) => {
            state.isMigrating = action.payload;
        },
        setCancelRequested: (state, action) => {
            state.cancelRequested = action.payload;
        },
        setMigrationLogs: (state, action) => {
            state.migrationLogs = action.payload;
        },
        addMigrationLog: (state, action) => {
            state.migrationLogs.push(action.payload);
        },
        updateMigrationLogs: (state, action) => {
            const newLogs = action.payload;
            newLogs.forEach(log => {
                const existingIndex = state.migrationLogs.findIndex(l => l.filePath === log.filePath);
                if (existingIndex !== -1) {
                    state.migrationLogs[existingIndex] = log;
                } else {
                    state.migrationLogs.push(log);
                }
            });
        },
        setErrors: (state, action) => {
            state.errors = action.payload;
        },
        addError: (state, action) => {
            state.errors.push(action.payload);
        },
        resetMigration: (state) => {
            state.sources = [];
            state.targets = [];
            state.outputDir = '';
            state.analysisResult = null;
            state.migrationProgress = 0;
            state.isAnalyzing = false;
            state.isMigrating = false;
            state.cancelRequested = false;
            state.migrationLogs = [];
            state.errors = [];
        },
    },
});

export const {
    addSource,
    removeSource,
    setSourcePath,
    setSources,
    addTarget,
    removeTarget,
    setTargets,
    setOutputDir,
    setAnalysisResult,
    setMigrationProgress,
    setIsAnalyzing,
    setIsMigrating,
    setCancelRequested,
    setMigrationLogs,
    addMigrationLog,
    updateMigrationLogs,
    setErrors,
    addError,
    resetMigration,
} = migrationSlice.actions;

export default migrationSlice.reducer;
